<template>
  <div class="wr-mbti-page">
    <header class="wr-mbti-top">
      <button type="button" class="wr-mbti-back" aria-label="返回" @click="goBack">
        <i class="el-icon-arrow-left"></i>
      </button>
      <h1 class="wr-mbti-title-bar">我的MBTI</h1>
      <span class="wr-mbti-top-spacer" aria-hidden="true" />
    </header>

    <div
      v-loading="pageLoading"
      class="wr-mbti-body"
      element-loading-text="分析中…"
    >
      <p v-if="loadError" class="wr-mbti-banner wr-mbti-banner--err">{{ loadError }}</p>
      <div v-else-if="userSummary || dimensions.length" class="wr-mbti-surface">
        <!-- MBTI 编码与人群占比：独立一块，与下文分析分开 -->
        <section v-if="showMbtiCodeCard" class="wr-mbti-card wr-mbti-card--mbti">
          <div class="wr-mbti-mbti-inner">
            <div class="wr-mbti-type-wrap">
              <div class="wr-mbti-type-square">
                <span class="wr-mbti-type-letters">{{ mbtiTypeLetters || '—' }}</span>
              </div>
              <div
                v-if="populationShareLine"
                class="wr-mbti-pct-chip"
                role="status"
              >
                {{ populationShareLine }}
              </div>
            </div>
          </div>
        </section>

        <!-- 标题 + 内容分析 -->
        <section v-if="userSummary" class="wr-mbti-card wr-mbti-card--analysis">
          <h2 class="wr-mbti-h2">{{ userSummary.title }}</h2>
          <p class="wr-mbti-analysis">{{ userSummary.analysis_text }}</p>
        </section>

        <section v-if="dimensions.length" class="wr-mbti-card wr-mbti-card--dims">
          <div
            v-for="(dim, idx) in dimensions"
            :key="idx"
            class="wr-mbti-dim"
          >
            <div class="wr-mbti-dim__side wr-mbti-dim__side--l">
              <span class="wr-mbti-code">{{ dim.left_code }}</span>
              <span class="wr-mbti-name">{{ dim.left_label }}</span>
            </div>
            <div class="wr-mbti-dim__track">
              <div
                class="wr-mbti-dim__fill"
                :class="dim.leaning === 'right' ? 'is-right' : 'is-left'"
                :style="{ width: barWidthPct(dim) + '%' }"
              >
                <span class="wr-mbti-dim__tail">
                  <span class="wr-mbti-dim__dot" aria-hidden="true" />
                  <span class="wr-mbti-dim__pct">{{ barWidthPct(dim) }}%</span>
                </span>
              </div>
            </div>
            <div class="wr-mbti-dim__side wr-mbti-dim__side--r">
              <span class="wr-mbti-code">{{ dim.right_code }}</span>
              <span class="wr-mbti-name">{{ dim.right_label }}</span>
            </div>
          </div>
        </section>
      </div>
      <p v-else-if="!pageLoading" class="wr-mbti-hint">暂无解读数据。</p>
    </div>
  </div>
</template>

<script>
import { getApiErrorMessage } from '@/utils/apiErrorMessage'
import { fetchMbtiResult } from '@/api/weekReport'

export default {
  name: 'WeekReportMbtiPage',
  data() {
    return {
      pageLoading: true,
      loadError: '',
      userSummary: null,
      dimensions: []
    }
  },
  computed: {
    mbtiTypeLetters() {
      const s = this.userSummary
      if (!s || typeof s !== 'object') return ''
      const t = s.personality_type != null ? String(s.personality_type).trim() : ''
      return t ? t.toUpperCase() : ''
    },
    /** 与下方维度条轨道同色 #e5e7eb，文案「仅占X%」 */
    populationShareLine() {
      const s = this.userSummary
      if (!s || typeof s !== 'object') return ''
      const p = s.population_percentage
      if (p == null || p === '') return ''
      const n = Number(p)
      if (!Number.isFinite(n)) return ''
      const str = Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10)
      return `仅占${str}%`
    },
    showMbtiCodeCard() {
      return !!(this.populationShareLine || this.mbtiTypeLetters)
    }
  },
  mounted() {
    this.runLoad()
  },
  methods: {
    goBack() {
      if (typeof window !== 'undefined' && window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/profile/week-report')
      }
    },
    barWidthPct(dim) {
      const n = Number(dim && dim.score)
      if (!Number.isFinite(n)) return 0
      return Math.max(0, Math.min(100, Math.round(n)))
    },
    async runLoad() {
      this.pageLoading = true
      this.loadError = ''
      let userId = ''
      try {
        userId =
          typeof localStorage !== 'undefined' ? String(localStorage.getItem('userId') || '').trim() : ''
      } catch (e) {
        void e
      }
      if (!userId) {
        this.loadError = '未登录或缺少用户标识，无法拉取 MBTI。'
        this.pageLoading = false
        return
      }
      try {
        const pack = await fetchMbtiResult()
        this.userSummary = pack.user_summary
        this.dimensions = pack.personality_dimensions
      } catch (e) {
        console.error(e)
        this.userSummary = null
        this.dimensions = []
        this.loadError = getApiErrorMessage(e, '人格分析加载失败')
      } finally {
        this.pageLoading = false
      }
    }
  }
}
</script>

<style scoped>
.wr-mbti-page {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background: #f0f2f5;
  box-sizing: border-box;
}

.wr-mbti-top {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: 52px;
  padding: 0 10px 0 6px;
  padding-top: env(safe-area-inset-top, 0px);
  border-bottom: 1px solid #e8e8e8;
  background: #fff;
}

.wr-mbti-back {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: rgba(0, 0, 0, 0.75);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.wr-mbti-back i {
  font-size: 20px;
}

.wr-mbti-title-bar {
  flex: 1;
  margin: 0;
  text-align: center;
  font-size: 17px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.88);
}

.wr-mbti-top-spacer {
  width: 40px;
  flex-shrink: 0;
}

.wr-mbti-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 16px 14px 24px;
  padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
  max-width: 560px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  position: relative;
}

.wr-mbti-banner {
  margin: 0 0 12px;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
}

.wr-mbti-banner--err {
  background: #fff1f2;
  color: #be123c;
}

.wr-mbti-hint {
  margin: 24px 0 0;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
}

.wr-mbti-surface {
  --wr-mbti-bar-fill: linear-gradient(90deg, #3b82f6 0%, #2563eb 55%, #1d4ed8 100%);
  padding: 4px 2px 8px;
  border-radius: 16px;
  background: #eff6ff;
  box-sizing: border-box;
}

.wr-mbti-card {
  background: #fff;
  border-radius: 16px;
  padding: 18px 16px 20px;
  box-shadow: 0 2px 16px rgba(15, 23, 42, 0.06);
  margin-bottom: 14px;
}

.wr-mbti-card:last-child {
  margin-bottom: 0;
}

.wr-mbti-h2 {
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.45;
  letter-spacing: -0.02em;
}

.wr-mbti-card--mbti {
  padding: 20px 16px 22px;
  overflow: visible;
}

.wr-mbti-mbti-inner {
  display: flex;
  justify-content: center;
  align-items: center;
}

.wr-mbti-type-wrap {
  position: relative;
  display: inline-block;
  vertical-align: top;
  overflow: visible;
}

/* 1:1 正方形：背景与维度条「超过 50% 时可见的彩色填充」同一渐变 + 立体 */
.wr-mbti-type-square {
  width: min(112px, 30vw);
  aspect-ratio: 1 / 1;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: var(--wr-mbti-bar-fill);
  border: 1px solid rgba(255, 255, 255, 0.28);
  box-shadow:
    0 3px 0 rgba(30, 64, 175, 0.24),
    0 10px 26px rgba(37, 99, 235, 0.32),
    0 2px 8px rgba(15, 23, 42, 0.12),
    inset 0 2px 5px rgba(255, 255, 255, 0.32),
    inset 0 -4px 10px rgba(29, 78, 216, 0.2);
}

.wr-mbti-type-letters {
  font-size: clamp(22px, 7vw, 30px);
  font-weight: 800;
  letter-spacing: 0.06em;
  color: #fff;
  line-height: 1;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

/* 人格占比：中心与正方形右上角重合，橘黄渐变，整体倾斜 45° */
.wr-mbti-pct-chip {
  position: absolute;
  left: 100%;
  top: 0;
  z-index: 2;
  padding: 5px 10px;
  border-radius: 8px;
  background: linear-gradient(135deg, #eff6ff 0%, #bfdbfe 45%, #60a5fa 75%, #3b82f6 100%);
  border: 1px solid rgba(191, 219, 254, 0.68);
  box-shadow:
    0 2px 0 rgba(29, 78, 216, 0.24),
    0 8px 20px rgba(37, 99, 235, 0.28),
    0 2px 6px rgba(15, 23, 42, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.55),
    inset 0 -3px 6px rgba(30, 64, 175, 0.14);
  font-size: 11px;
  font-weight: 800;
  line-height: 1.2;
  color: #1e3a8a;
  white-space: nowrap;
  transform: translate(calc(-50% - 9px), calc(-50% + 12px)) rotate(45deg);
  transform-origin: center center;
}

.wr-mbti-card--analysis .wr-mbti-h2 {
  margin-bottom: 10px;
}

.wr-mbti-analysis {
  margin: 0;
  font-size: 14px;
  line-height: 1.75;
  color: #334155;
  text-align: justify;
}

.wr-mbti-card--dims {
  padding-top: 16px;
  padding-bottom: 18px;
}

.wr-mbti-dim {
  display: grid;
  grid-template-columns: 48px minmax(0, 1fr) 48px;
  gap: 6px 10px;
  align-items: center;
  margin-bottom: 20px;
}

.wr-mbti-dim:last-child {
  margin-bottom: 0;
}

.wr-mbti-dim__side {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-width: 0;
}

.wr-mbti-code {
  font-size: 17px;
  font-weight: 800;
  color: #1e3a8a;
  line-height: 1.15;
}

.wr-mbti-name {
  margin-top: 2px;
  font-size: 11px;
  color: #475569;
  line-height: 1.2;
}

.wr-mbti-dim__track {
  position: relative;
  height: 20px;
  background: #dbeafe;
  border-radius: 10px;
  overflow: hidden;
}

.wr-mbti-dim__fill {
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 10px;
  background: var(--wr-mbti-bar-fill);
  box-sizing: border-box;
  min-width: 32px;
}

.wr-mbti-dim__fill.is-left {
  left: 0;
}

.wr-mbti-dim__fill.is-right {
  right: 0;
  left: auto;
}

/* 白点 + 百分比成组，贴在彩色区朝向「中间」的一侧（左长条靠右、右长条靠左） */
.wr-mbti-dim__tail {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  flex-direction: row;
  gap: 2px;
  white-space: nowrap;
}

.wr-mbti-dim__fill.is-left .wr-mbti-dim__tail {
  right: 4px;
}

.wr-mbti-dim__fill.is-right .wr-mbti-dim__tail {
  left: 4px;
}

.wr-mbti-dim__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.wr-mbti-dim__pct {
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  color: #fff;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.18);
}
</style>
